/**
 * The world map and zone maps of one open-world area, for location pickers.
 *
 * Cached per area for the page's lifetime (maps only change with a platform
 * release) and shared between callers. A failed load is not cached, so the next
 * mount retries.
 */

import { useEffect, useState } from 'react';
import type { PlatformAreaMapsResponse } from '@swtorbit/shared-socket-events';
import { fetchPlatformAreaMaps } from '../utils/platformApi';

const cache = new Map<number, Promise<PlatformAreaMapsResponse>>();

function loadAreaMaps(logAreaId: number): Promise<PlatformAreaMapsResponse> {
  let pending = cache.get(logAreaId);
  if (!pending) {
    pending = fetchPlatformAreaMaps(logAreaId).catch((error: unknown) => {
      cache.delete(logAreaId);
      throw error;
    });
    cache.set(logAreaId, pending);
  }
  return pending;
}

export interface UsePlatformAreaMaps {
  maps: PlatformAreaMapsResponse | null;
  loading: boolean;
  error: string | null;
}

export function usePlatformAreaMaps(logAreaId: number | null): UsePlatformAreaMaps {
  const [state, setState] = useState<UsePlatformAreaMaps>({ maps: null, loading: false, error: null });

  useEffect(() => {
    if (logAreaId === null) {
      setState({ maps: null, loading: false, error: null });
      return;
    }
    let live = true;
    setState({ maps: null, loading: true, error: null });
    loadAreaMaps(logAreaId)
      .then((maps) => { if (live) setState({ maps, loading: false, error: null }); })
      .catch((e: unknown) => {
        if (live) setState({ maps: null, loading: false, error: e instanceof Error ? e.message : 'Failed to load maps' });
      });
    return () => { live = false; };
  }, [logAreaId]);

  return state;
}
